import { v, type Infer } from "convex/values";

/**
 * Reader's target language for gloss/translation, a lowercase ISO 639 /
 * BCP-47-ish code. Kept an open string rather than a literal union: readers
 * may request any language, and the aiCache key includes it verbatim.
 */
export const targetLangValidator = v.string();
export type TargetLang = Infer<typeof targetLangValidator>;

/** Global reader preference default (SPEC "Translation / gloss"). */
export const DEFAULT_TARGET_LANG: TargetLang = "en";

/** Reader target-language display names for the gloss/translation prompt. */
const TARGET_LANG_NAME: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  zh: "Chinese (Simplified)",
  ja: "Japanese",
};

/**
 * Display name for a target-language code, for the gloss prompt. An unknown
 * code is reachable only via a direct API call (the shipped provider only
 * offers known codes), but must NOT fall back to a display name: doing so
 * would prompt for — and publicly cache — an English result under the
 * unknown code's own cache key, silently mis-serving any future reader of
 * that language. Instead it names the raw code in the prompt, so the
 * result (right or wrong) is generated for, and cached under, its own key.
 */
export function targetName(code: TargetLang): string {
  return TARGET_LANG_NAME[code] ?? `the language with code "${code}"`;
}
